/* A page from Sites, as a build-ready brief for a site builder.
 *
 * WHAT THIS IS FOR. GoHighLevel's API cannot create funnel pages — funnels and
 * pages are read-only — so a website cannot be pushed the way an offer can.
 * What their AI builder takes instead is a JSON brief, and it told us its own
 * schema when asked (docs/site-brief-spec.md). That schema is deliberately
 * builder-agnostic: it describes what the page IS, not how any one app renders
 * it. So this is not a GHL integration. It is a portable spec that happens to
 * work with GHL today, and with whatever reads it next.
 *
 * WHY JSON RATHER THAN PROSE, and it is not a preference. The same builder,
 * on the same day: handed prose it invented "Marcus Reyes, CEO, Northwind
 * Logistics" and a 4.9/5 rating; handed structure it reproduced her page word
 * for word. Fields get mapped. Paragraphs get interpreted.
 *
 * NOTHING HERE WRITES ANYTHING. It reads what she has already locked and
 * arranges it. Every string in the output is hers.
 */

import { kindOf, pictureOf } from "./websiteSectionKinds";
import { siteTokens } from "./siteTokens";

/* ── Brill's section kinds → the builder's section types ─────────────────────
   Theirs are named for what a section DOES; hers for what she puts in one.
   Mostly the same thing, with two calls that are hers (2026-09-22):

   FREEBIE IS A CTA. A freebie is an offer, a reason and a button — which is a
   call to action. The other candidate was a second hero, and it is not one.

   OTHER IS AN EXPLAINER, by elimination: `other` is the escape hatch, and
   `explainer` is their general-purpose type. It is what her "Missing Link"
   section became when the builder saw it. */
/* ── What KIND of business the site is for ───────────────────────────────────
   The builder's eight audience presets (docs/site-brief-spec.md §2). Each one
   steers tone, which sections it emphasises, what trust signals it reaches for
   and what imagery it picks — so it is the single most consequential field in
   the brief after the copy itself.

   HERS TO CHOOSE, NEVER INFERRED. Brill knows her niche and could guess; the
   guess would be confident and sometimes wrong, and wrong here does not look
   wrong — it looks like a page written for somebody else. Their own fallback
   is `general-coach`, which means "let the content carry it", and defaulting
   to that is honest in a way defaulting to a guess is not.

   The labels are theirs. The second line is what it actually changes, because
   "CEO / Strategy Consultant" does not tell her which to pick and "direct,
   no-fluff, peer-level — leads with frameworks and outcomes" does. */
export const SITE_AUDIENCES = [
  { id: "general-coach", label: "General coach or consultant", hint: "Neutral. Your own words carry the page." },
  { id: "health-coach", label: "Health & wellness coach", hint: "Warm and non-shaming — transformation stories, a simple process, a gentle invitation." },
  { id: "corporate-coach", label: "Corporate or leadership coach", hint: "Confident and evidence-led — results, methodology, client logos." },
  { id: "finance-consultant", label: "Finance or CFO consultant", hint: "Authoritative and calm — credentials, a services matrix, numbers." },
  { id: "ceo-consultant", label: "CEO or strategy consultant", hint: "Direct and peer-level — frameworks, outcomes, no coaching language." },
  { id: "therapist", label: "Therapist or clinician", hint: "Grounded and trauma-aware — specialties, approach, logistics, no pressure to book." },
  { id: "parent-family-coach", label: "Parent or family coach", hint: "Empathetic and real — stories from families, simple frameworks, no shame." },
  { id: "creative-consultant", label: "Creative or brand consultant", hint: "Expressive and design-literate — the work itself, process, philosophy." },
];

export const isSiteAudience = (id) => SITE_AUDIENCES.some((a) => a.id === id);

const TYPE_OF = {
  banner: "hero",
  about: "about",
  services: "services",
  freebie: "cta",
  testimonials: "testimonials",
  faq: "faq",
  cta: "cta",
  contact: "contact",
  other: "explainer",
};

const str = (v) => String(v ?? "").trim();
const rows = (v) => (Array.isArray(v) ? v : []);
/* Prose she typed as paragraphs is stored as one string with blank lines
   between. The builder wants an array, and splitting on blank lines is what
   she meant by pressing return twice. */
const paras = (v) => str(v).split(/\n{2,}/).map((p) => p.trim().replace(/\n/g, " ")).filter(Boolean);

/* An anchor a CTA can point at. Their `scroll:#id` wants an id that exists, so
   ids come from the section type and nothing else invents one. */
const anchorFor = (type) => `#${type}`;

/**
 * One section, as their shape. Returns null for a section with nothing in it —
 * an empty block on a brief is an instruction to invent something.
 */
function sectionOf(section, { contactAnchor }) {
  /* `kindOf` returns the id itself, not a definition object. */
  const type = TYPE_OF[kindOf(section)] || "explainer";
  const c = section?.content || {};
  const cta = (label) => (str(label) ? { label: str(label), ...(contactAnchor ? { action: `scroll:${contactAnchor}` } : {}) } : undefined);

  const base = { type };
  if (type === "hero") {
    if (!str(c.head) && !str(c.sub)) return null;
    return {
      ...base,
      eyebrow: str(c.eyebrow) || undefined,
      headline: str(c.head),
      subhead: str(c.sub) || undefined,
      cta: cta(c.btn),
    };
  }
  if (type === "cta") {
    if (!str(c.head) && !str(c.sub)) return null;
    return { ...base, headline: str(c.head), subhead: str(c.sub) || undefined, cta: cta(c.btn) };
  }
  if (type === "about") {
    const p = rows(c.paras).map(str).filter(Boolean);
    if (!p.length && !str(c.head)) return null;
    return { ...base, headline: str(c.head) || undefined, paragraphs: p, photoUrl: pictureOf(c) || str(c.photo) || "" };
  }
  if (type === "services") {
    const items = rows(c.items)
      .map((r) => ({ title: str(r?.name), description: str(r?.line), price: str(r?.price) || undefined }))
      .filter((i) => i.title || i.description);
    if (!items.length) return null;
    return { ...base, headline: str(c.head) || undefined, items };
  }
  if (type === "testimonials") {
    /* A quote is the one thing that must never be rewritten — it is somebody
       else's words, picked from the Library with permission. Anything without
       words is dropped rather than sent as an empty slot to fill. */
    const items = rows(c.quotes)
      .map((r) => ({ quote: str(r?.text), name: str(r?.who), photoUrl: str(r?.photo) || undefined }))
      .filter((i) => i.quote);
    if (!items.length) return null;
    return { ...base, headline: str(c.head) || undefined, items };
  }
  if (type === "faq") {
    const items = rows(c.qs).map((r) => ({ question: str(r?.q), answer: str(r?.a) })).filter((i) => i.question);
    if (!items.length) return null;
    return { ...base, headline: str(c.head) || undefined, items };
  }
  if (type === "contact") {
    if (!str(c.head) && !str(c.sub)) return null;
    return {
      ...base,
      headline: str(c.head) || undefined,
      intro: str(c.sub) || undefined,
      /* The minimum a lead needs to become a contact. Brill does not hold a
         form design, and inventing fields she never asked for is the same
         mistake as inventing copy. */
      formFields: [
        { name: "fullName", label: "Full name", type: "text", required: true },
        { name: "email", label: "Email", type: "email", required: true },
        { name: "message", label: "How can I help?", type: "textarea", required: false },
      ],
      submitLabel: str(c.btn) || "Send",
      integration: { formTracking: "enabled", calendar: "disabled" },
    };
  }
  // explainer — `other`, and anything unmapped
  const body = paras(c.text);
  if (!body.length && !str(c.head)) return null;
  return { ...base, headline: str(c.head) || undefined, paragraphs: body };
}

/**
 * @param page      the website page
 * @param sections  its sections, in order
 * @param skin      what readBrandSkin returned (brandSkin.js)
 * @param fonts     { heading, body } from the same place
 * @param identity  { businessName, ownerName, ownerRole, ownerCredentials, tagline }
 * @param mode      "dark" | "light"
 * @returns { brief, notes, missing }
 */
export function siteBrief({ page, sections = [], skin = {}, fonts = {}, identity = {}, mode = "dark" } = {}) {
  const { tokens, notes: paletteNotes } = siteTokens(skin, { mode });
  const notes = [...paletteNotes];
  const missing = [];

  const built = [];
  const kinds = sections.map((s) => kindOf(s));
  const hasContact = kinds.includes("contact");
  /* A button scrolls to a section the page HAS: contact, else a call to
     action (a freebie is one), else nowhere — it keeps its words, loses its
     link, and she is told. It used to point at #cta regardless, so a page
     without one sent every button to an anchor nothing carried (found
     2026-10-27, the same day as the Wix spec's copy of it). */
  const contactAnchor = hasContact ? anchorFor("contact")
    : kinds.some((k) => k === "cta" || k === "freebie") ? anchorFor("cta") : null;
  for (const s of sections) {
    const out = sectionOf(s, { contactAnchor });
    if (out) built.push(out);
  }
  if (!contactAnchor && built.some((b) => b.cta)) {
    notes.push("Your buttons don't link anywhere yet — this page has no contact or call-to-action section for them to go to.");
  }

  /* WHAT THEY REQUIRE AND SHE DOES NOT HOLD, named rather than filled. Their
     validation list (§9) is the source of this; a brief that quietly ships
     without them is a brief the builder completes on her behalf. */
  if (!str(identity.businessName)) missing.push("a business name");
  if (!str(identity.ownerName)) missing.push("your name");
  if (!built.length) missing.push("at least one section with something written in it");
  if (!built.some((b) => b.type === "hero")) missing.push("a banner — their builder wants a hero to open on");

  /* SEO: required by them, held by Brill nowhere. Derived from the page and
     the first thing it says, and TRIMMED to their limits rather than sent long
     and silently cut. */
  const hero = built.find((b) => b.type === "hero");
  /* HERS IF SHE WROTE ONE (supabase/0027), derived if she did not. Derived is a
     good default and a bad only-option: the sentence that makes someone click
     is not always the sentence that opens the page. */
  const title = clip(str(page?.seoTitle) || `${str(page?.title)}${identity.businessName ? ` — ${str(identity.businessName)}` : ""}`, 60);
  const description = clip(str(page?.seoDescription) || str(hero?.subhead) || str(hero?.headline) || str(identity.tagline), 160);
  if (!description) missing.push("a subhead on your banner — it becomes the page's description in search results");

  const brief = {
    version: "1.0",
    singlePage: true,
    identity: {
      businessName: str(identity.businessName),
      tagline: str(identity.tagline) || undefined,
      ownerName: str(identity.ownerName),
      ownerRole: str(identity.ownerRole) || undefined,
      ownerCredentials: str(identity.ownerCredentials) || undefined,
      ownerPhotoUrl: str(identity.ownerPhotoUrl) || "",
      logoHorizontalUrl: str(identity.logoUrl) || undefined,
      /* Her Me → Brand icon (2026-10-13). The builder's format has no slot for
         a dark logo, so none is invented. */
      faviconUrl: str(identity.iconUrl) || undefined,
    },
    theme: {
      /* AUDIENCE IS HERS, NOT A LOOKUP. Which of their eight presets a
         business fits is a judgement about a market, and `general-coach` is
         their own honest fallback. Nothing here guesses at it. */
      audience: isSiteAudience(str(identity.audience)) ? str(identity.audience) : "general-coach",
      aesthetic: mode === "dark" ? "modern-dark" : "minimal",
      palette: { source: "brand", brand: tokens },
      fontPair: { display: str(fonts.heading) || undefined, body: str(fonts.body) || undefined },
    },
    pages: [{ slug: str(page?.slug) || "home", sections: built }],
    global: {
      navLinks: built
        .filter((b) => b.headline && b.type !== "hero")
        .map((b) => ({ label: b.headline, href: anchorFor(b.type) })),
      socialLinks: [],
    },
    seo: { defaultTitle: title, defaultDescription: description, keywords: [], robots: "index" },
    integrations: { formTracking: hasContact ? "enabled" : "disabled", calendar: "disabled" },
  };

  if (!isSiteAudience(str(identity.audience))) {
    notes.push("Audience is set to “general coach” — pick the one that fits and the builder will lean the whole page toward it.");
  }
  if (!str(identity.ownerPhotoUrl)) {
    notes.push("No photograph of you, so the About section will get whatever the builder decides to put there.");
  }

  return { brief, notes, missing };
}

/* Their limits are real: 60 for a title, 160 for a description. Cut on a word,
   because a title that ends mid-word reads as broken rather than as trimmed. */
function clip(text, max) {
  const t = str(text);
  if (t.length <= max) return t;
  const cut = t.slice(0, max);
  const at = cut.lastIndexOf(" ");
  return (at > max * 0.6 ? cut.slice(0, at) : cut).trim();
}

/* ── THE SAME PAGE, AS A SPEC FOR WIX'S AI BUILDER ───────────────────────────
   Her call, 2026-10-27: "wouldn't it just write a spec? not export". Wix's API
   cannot build her a page either, so this is the brief move again in Wix's
   own shape — the JSON she pasted on 2026-09-29 (reference: Harmony's page
   spec). It writes nothing and connects to nothing; it is her page, on the
   clipboard, for her to paste into Wix.

   Two things her pasted spec has no slot for, ADDED and named in `notes` so
   she can tell Wix what they are: `image` on a section (a public Brill
   picture link, supabase/0047 — the reason pictures live in Brill) and
   `site.brand` (her colours and fonts). `elements` and Harmony ids are only
   for mapping onto an EXISTING Wix site, so they are left out.

   The same rule as the brief: an empty section is never sent, a testimonial
   is never anything but her Library's words. */
const WIX_TYPE_OF = {
  banner: "hero",
  about: "about",
  services: "services",
  freebie: "cta",
  testimonials: "testimonials",
  faq: "faq",
  cta: "cta",
  contact: "contact",
  other: "text",
};

function wixSectionOf(section, { ctaTarget }) {
  const kind = kindOf(section);
  const type = WIX_TYPE_OF[kind] || "text";
  const c = section?.content || {};
  const pic = pictureOf(c) || (type === "about" ? str(c.photo) : "");
  const primaryCta = str(c.btn)
    ? { label: str(c.btn), action: ctaTarget ? { type: "anchor", target: ctaTarget, openInNewTab: false } : undefined }
    : undefined;
  const base = { type, anchorId: type, image: pic ? { url: pic } : undefined };

  if (type === "hero" || type === "cta" || type === "contact") {
    if (!str(c.head) && !str(c.sub)) return null;
    return { ...base, eyebrow: str(c.eyebrow) || undefined, headline: str(c.head) || undefined, subhead: str(c.sub) || undefined, primaryCta };
  }
  if (type === "about") {
    const p = rows(c.paras).map(str).filter(Boolean);
    if (!p.length && !str(c.head)) return null;
    return { ...base, headline: str(c.head) || undefined, paragraphs: p };
  }
  if (type === "services") {
    const items = rows(c.items)
      .map((r) => ({ title: str(r?.name), subtitle: str(r?.price) || undefined, body: str(r?.line) || undefined }))
      .filter((i) => i.title || i.body);
    if (!items.length) return null;
    return { ...base, headline: str(c.head) || undefined, items };
  }
  if (type === "testimonials") {
    const testimonials = rows(c.quotes)
      .map((r) => ({ quote: str(r?.text), name: str(r?.who) || undefined, image: str(r?.photo) ? { url: str(r.photo) } : undefined }))
      .filter((t) => t.quote);
    if (!testimonials.length) return null;
    return { ...base, headline: str(c.head) || undefined, testimonials };
  }
  if (type === "faq") {
    /* Their spec has no FAQ shape; a question and its answer are an item's
       title and body, which is what an accordion is drawn from. */
    const items = rows(c.qs).map((r) => ({ title: str(r?.q), body: str(r?.a) || undefined })).filter((i) => i.title);
    if (!items.length) return null;
    return { ...base, headline: str(c.head) || undefined, items };
  }
  const body = paras(c.text);
  if (!body.length && !str(c.head)) return null;
  return { ...base, headline: str(c.head) || undefined, paragraphs: body };
}

/**
 * @param page      the website page
 * @param sections  its sections, in order
 * @param skin      what readBrandSkin returned (brandSkin.js)
 * @param fonts     { heading, body }
 * @param identity  { businessName, ownerName, tagline, audience, ownerPhotoUrl, logoUrl }
 * @returns { spec, notes, missing }
 */
export function wixSpec({ page, sections = [], skin = {}, fonts = {}, identity = {}, mode = "light" } = {}) {
  const { tokens } = siteTokens(skin, { mode });
  const notes = [];
  const missing = [];

  /* A button scrolls to her contact section when the page has one, otherwise
     to a call to action — never to an anchor nothing carries. With neither,
     the button keeps its words and loses its link, and she is told. (Shipped
     first pointing at "cta" regardless; caught on Jenn's four-section page,
     2026-10-27, before anyone pasted it.) */
  const kinds = sections.map((s) => kindOf(s));
  const ctaTarget = kinds.includes("contact") ? "contact" : kinds.some((k) => k === "cta" || k === "freebie") ? "cta" : null;
  const built = sections.map((s) => wixSectionOf(s, { ctaTarget })).filter(Boolean);
  if (!ctaTarget && built.some((b) => b.primaryCta)) {
    notes.push("Your buttons don't link anywhere yet — this page has no contact or call-to-action section for them to go to.");
  }

  if (!str(identity.businessName)) missing.push("a business name");
  if (!built.length) missing.push("at least one section with something written in it");
  if (!built.some((b) => b.type === "hero")) missing.push("a banner to open the page on");

  const audience = SITE_AUDIENCES.find((a) => a.id === str(identity.audience));
  const hero = built.find((b) => b.type === "hero");
  const spec = {
    site: {
      businessName: str(identity.businessName),
      /* Her pick in Sites, or nothing. A guessed business type is how a page
         ends up written for somebody else. */
      businessType: audience && audience.id !== "general-coach" ? audience.label : undefined,
      businessDescription: str(identity.tagline) || str(hero?.subhead) || undefined,
      ownerName: str(identity.ownerName) || undefined,
      ownerPhoto: str(identity.ownerPhotoUrl) ? { url: str(identity.ownerPhotoUrl) } : undefined,
      logo: str(identity.logoUrl) ? { url: str(identity.logoUrl) } : undefined,
      brand: { colors: tokens, fonts: { heading: str(fonts.heading) || undefined, body: str(fonts.body) || undefined } },
    },
    pages: [{
      slug: str(page?.slug) || "home",
      title: str(page?.title) || undefined,
      sections: built,
    }],
    nav: built
      .filter((b) => b.headline && b.type !== "hero")
      .map((b) => ({ label: b.headline, hrefType: "anchor", target: b.anchorId })),
  };

  const pictures = built.filter((b) => b.image).length + built.reduce((n, b) => n + rows(b.testimonials).filter((t) => t.image).length, 0);
  if (pictures) {
    notes.push(`${pictures} picture${pictures === 1 ? "" : "s"} go${pictures === 1 ? "es" : ""} with it as links. Tell Wix to use each section's "image" as that section's picture.`);
  } else {
    notes.push("No pictures on this page, so Wix will pick its own stock photos.");
  }
  if (!audience || audience.id === "general-coach") notes.push("No kind of business chosen, so Wix gets none — pick one in Me → Business and it will lean the page toward it.");

  return { spec: JSON.parse(JSON.stringify(spec)), notes, missing };
}

/* ── THE PAGE, AS A WRITTEN PROMPT — FOR WIX, WORDPRESS OR ANY AI BUILDER ────
   Her calls, 2026-10-27: "for wix it needs to not be a json output can be a
   prompt", then "you could also do a GENERAL prompt and then it could go into
   whatever wordpress". Nothing in it names a builder, so ONE prompt serves
   them all; Wix's AI was simply the first to take it. So this WRITES OUT wixSpec — never reads the
   page a second time — and the button, picture and empty-section rules stay
   in one place. Stricter than the GHL prompt (her words: "for GHL there is
   more flexibility"): no placeholders, no lean, nothing it is not given.
   Proven by hand on Jenn's homepage first (Design/jenn-wix-prompt.md). */
const WIX_COLOUR_LINES = [
  ["background", "Page background"],
  ["foreground", "Body text"],
  ["primary", "Button background and headings", "primaryForeground"],
  ["accent", "Small highlights only"],
  ["surface", "Cards and panels", null, "border"],
  ["mutedForeground", "Secondary text"],
];

/* WHAT WIX DID WITH THE FIRST ONE (Jenn's page, 2026-10-27): it dropped her
   banner photo and logo for stock, made up a photo of a woman for "Meet
   Jenn", turned "Beyond the Armor" into "Past the Defense", added commas,
   and put Home / Services in the menu. Each rule below answers one of those.
   A section that should carry a picture and has none gets a labelled
   PLACEHOLDER — her call for GHL too: a spot is easier to fill than to make. */
const PICTURE_SLOT = { hero: "Banner picture", about: "Photo of" };

/* WIX BUILDS IN SECTIONS (her read, 2026-10-27, from its Add Section panel:
   "you need to speak to the sections for wix"). Its designs sit under these
   categories, so each section names the one to build it from. The words are
   plain enough to mean the same in any builder. Services says NOT a booking
   design: those cards carry Wix Bookings and a "Book Now" she never wrote.
   The FAQ lives under Reviews & Credibility there. */
const SECTION_CATEGORY = {
  hero: "Welcome",
  about: "About",
  services: "Services",
  testimonials: "Reviews & Credibility",
  faq: "Reviews & Credibility",
  cta: "Call to Action",
  contact: "Contact Info",
  text: "About",
};
const CATEGORY_NOTE = {
  services: " — a plain one, not a booking design",
  faq: ", its FAQ design",
};

function wixSectionText(s, n, anchorName, owner) {
  const out = [];
  const head = (label) => out.push(`${n}. ${label.toUpperCase()}`);
  head(s.type === "hero" ? "Banner" : s.headline || s.type);
  out.push(`Section: ${SECTION_CATEGORY[s.type] || "Blank"}${CATEGORY_NOTE[s.type] || ""}.`);
  if (s.image?.url) out.push(`Picture: use exactly this image — ${s.image.url}. If you cannot load it, leave a placeholder labelled "${s.type === "about" ? `Photo of ${owner || "the owner"}` : "Picture"}", never a stock photo.`);
  else if (PICTURE_SLOT[s.type]) out.push(`Picture: none yet. Leave a placeholder labelled "${s.type === "about" ? `Photo of ${owner || "the owner"}` : PICTURE_SLOT[s.type]}" — never a stock photo, and never a made-up photo of a person.`);
  if (s.eyebrow) out.push(`Small line above the headline: ${s.eyebrow}`);
  if (s.headline) out.push(`${s.type === "hero" ? "Headline" : "Heading"}: ${s.headline}`);
  if (s.subhead) out.push(`Text under it: ${s.subhead}`);
  if (s.primaryCta) {
    const to = s.primaryCta.action?.target;
    out.push(`Button: ${s.primaryCta.label}.${to ? ` Make it scroll to the "${anchorName(to)}" section.` : " It has no link yet — leave it unlinked."}`);
  }
  for (const p of s.paragraphs || []) out.push("", p);
  if (s.items?.length) {
    /* Not "opens to show its answer": Harmony's help says it has no accordion
       yet, though its FAQ design looks like one — so the category names the
       design and the words ask only for what every builder can draw. */
    out.push("", s.type === "faq" ? "List each question with its answer under it:" : `Show ${s.items.length} card${s.items.length === 1 ? "" : "s"}, each with its title${s.items.some((i) => i.subtitle) ? ", price" : ""} and description:`);
    for (const i of s.items) out.push("", ...[i.title, i.subtitle, i.body].filter(Boolean));
  }
  if (s.testimonials?.length) {
    out.push("", "Use these testimonials word for word. Do not add any others:");
    for (const t of s.testimonials) out.push("", `"${t.quote}"`, ...(t.name ? [`— ${t.name}`] : []), ...(t.image?.url ? [`Photo: ${t.image.url}`] : []));
  }
  return out.join("\n");
}

/** @returns { prompt, notes, missing } — notes and missing are wixSpec's. */
export function sitePrompt(args = {}) {
  const { spec, notes, missing } = wixSpec(args);
  const { site, pages, nav } = spec;
  const page = pages[0];
  const name = site.businessName || site.ownerName;
  const anchorName = (id) => page.sections.find((s) => s.anchorId === id)?.headline || id;

  const lines = [];
  const order = page.sections.map((s) => (s.type === "hero" ? "a banner" : s.headline ? `"${s.headline}"` : `a ${s.type} section`));
  lines.push(`Build a one-page website${name ? ` for ${name}` : ""}${site.businessType ? `, a ${site.businessType.replace(/^[A-Z](?![A-Z])/, (c) => c.toLowerCase())}` : ""}. The page has ${order.length} section${order.length === 1 ? "" : "s"} in this order: ${order.join(", ")}. Nothing else.`);
  lines.push("", "Rules:",
    "- Use the words below exactly as written, punctuation included. Do not rewrite, shorten, polish or add to them — not even a small line or a comma.",
    "- Do not add any sections, testimonials, statistics, client logos, a newsletter signup or a contact form. If something isn't listed below, leave it out.",
    "- Do not make up prices, credentials or quotes.",
    "- Use only the pictures linked below. Never make up a photo of a person. Where a section has no picture, leave a labelled placeholder.",
    "- Build each section from a ready-made section design in the category it names (in Wix: Add → Sections), then put these words into it.");

  const c = site.brand?.colors || {};
  const colourLines = WIX_COLOUR_LINES.filter(([k]) => c[k]).map(([k, label, on, edge]) =>
    `- ${label}: ${c[k]}${on && c[on] ? `, with ${c[on]} text on the buttons` : ""}${edge && c[edge] ? `, with ${c[edge]} borders` : ""}`);
  const f = site.brand?.fonts || {};
  const fontLine = [f.heading && `Heading font: ${f.heading}.`, f.body && `Body font: ${f.body}.`].filter(Boolean).join(" ");
  if (colourLines.length || fontLine || site.logo) {
    lines.push("", "LOOK AND FEEL", ...colourLines);
    if (fontLine) lines.push(`- ${fontLine}`);
    if (site.logo) lines.push(`- Logo: use this image in the header, not the name typed out — ${site.logo.url}`);
  }

  if (nav.length) lines.push("", "NAVIGATION", `The menu has exactly ${nav.length} link${nav.length === 1 ? "" : "s"}, each scrolling down this page: ${nav.map((l) => `"${l.label}"`).join(", ")}. No Home, no other pages.`);
  page.sections.forEach((s, i) => lines.push("", wixSectionText(s, i + 1, anchorName, site.ownerName || name)));
  lines.push("", "FOOTER", `Just ${site.logo ? "the logo and " : ""}${name ? `"${name}"` : "the business name"}. No other links or text.`);
  lines.push("", "Before you finish, check every line on the page against this prompt and put back anything that was changed.");

  return {
    prompt: lines.join("\n"),
    /* wixSpec's notes, said of any builder. */
    notes: notes.map((n) => n
      .replace(` Tell Wix to use each section's "image" as that section's picture.`, " Each one is named in the prompt beside its section.")
      .replace(/Wix/g, "the builder")),
    missing,
  };
}
